import React from 'react';
import PropTypes from 'prop-types';
import { Link, graphql } from 'gatsby';
import styled from '@emotion/styled';
import { TypographyStyle } from 'react-typography';

import { m3medicalTheme } from '../../utils/typography';
import { Card } from '../../components/Card/Card';
import Layout from '../../components/Layout';

const PageWrapper = styled.div`
  background-color: #eee;
  text-align: center;
  color: rgba(0, 0, 0, 0.78);
`;
const NotFoundWrapper = styled.div`
  text-align: center;
  @media only screen and (min-width: 768px) {
    text-align: left;
  }
`;

const NotFound = styled.div`
  margin: 0;
  padding: 0;
`;

const H1Missing = styled.h1`
  margin-bottom: 0;
  font-size: 100px;
  color: #002f63;
  line-height: 1.2em;
  margin: 0 0 7px 0;
  font-weight: 700;
  font-family: 'Avenir Next', 'Helvetica Neue', 'Segoe UI', 'Helvetica', 'Arial',
    sans-serif;
`;

const H2 = styled.h2`
  font-size: 47px;
  color: #002f63;
  text-transform: uppercase;
  line-height: 1.2em;
  font-weight: 400;
  margin: 0 0 12px 0;
`;

const H3 = styled.h3`
  color: #333;
  font-size: 21px;
  line-height: 1.2em;
  font-weight: 400;
  margin: 0 0 12px 0;
  font-family: 'Avenir Next', 'Helvetica Neue', 'Segoe UI', 'Helvetica', 'Arial',
    sans-serif;
  a:link,
  a:visited,
  a:active {
    color: #029dd9;
    font-weight: 600;
    text-decoration: none;
  }
  a:hover {
    transition: all 0.5s ease;
    text-decoration: underline;
  }
  span {
    text-transform: lowercase;
  }
`;

const ConferenceRow = styled.div`
  text-align: left;
`;

const AngryDoctor = styled.img`
  @media only screen and (min-width: 768px) {
    float: left;
    margin-right: 30px;
    margin-bottom: 40px;
  }
  width: 138px;
  height: 282px;
  margin-bottom: 15px;
`;

const NotFoundPageM3M = ({ data }) => {
  const articleIcon = data.articleIcon && data.articleIcon.publicURL;
  const videoIcon = data.videoIcon && data.videoIcon.publicURL;
  const angryDoctor = data.angryDoctor && data.angryDoctor.publicURL;

  const conferences = data.allContentfulConference.edges;

  return (
    <Layout googleFontsUsed={m3medicalTheme}>
      <PageWrapper>
        <TypographyStyle typography={m3medicalTheme} />
        <div className="container">
          <NotFoundWrapper>
            <NotFound>
              <AngryDoctor src={angryDoctor} />
              <H1Missing>404</H1Missing>
              <H2>{data.contentfulGlobalGenerics.oops}</H2>
              <H3>{data.contentfulGlobalGenerics.couldntFindPage}</H3>
              {data.contentfulGlobalGenerics.findMore && (
                <H3>
                  <Link to="/">{data.contentfulGlobalGenerics.findMore}</Link>{' '}
                  <span>
                    {data.allContentfulConference &&
                      data.contentfulGlobalGenerics.enjoyLatest}
                  </span>
                </H3>
              )}
            </NotFound>
          </NotFoundWrapper>

          {data.allContentfulConference &&
            data.contentfulGlobalGenerics.enjoyLatest && (
              <ConferenceRow
                key="conference-index-row-one"
                className="c-conference-fb__row">
                {conferences.map(({ node }) => (
                  <Card
                    isM3M
                    key={`Card-${node.id}`}
                    node={node}
                    imageCreditGlobal={
                      data.contentfulGlobalGenerics.imageCredit
                    }
                    articleIcon={articleIcon}
                    videoIcon={videoIcon}
                  />
                ))}
              </ConferenceRow>
            )}
        </div>
      </PageWrapper>
    </Layout>
  );
};

export default NotFoundPageM3M;

NotFoundPageM3M.propTypes = {
  data: PropTypes.object
};

export const pageQuery = graphql`
  query NotFoundQueryM3M($locale: String!, $showAllContent: Boolean!) {
    allContentfulConference(
      filter: { node_locale: { eq: $locale }, placeholder: { ne: true } }
      sort: { fields: [dateOfConference], order: DESC }
      limit: 3
    ) {
      edges {
        node {
          id
          title
          node_locale
          slug
          intro
          articlesOnly
          dateOfConference
          sponsor {
            description {
              description
            }
          }
          thumbnailImage {
            fluid(maxWidth: 740, maxHeight: 400, quality: 80) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          heroImage {
            fluid(maxWidth: 740, maxHeight: 400, quality: 80) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          thumbnailImageCredit
          conferenceImageCredits
        }
      }
    }
    angryDoctor: file(relativePath: { eq: "404-doctor.svg" }) {
      publicURL
    }
    articleIcon: file(relativePath: { eq: "articleIcon.svg" }) {
      publicURL
    }
    videoIcon: file(relativePath: { eq: "videoIcon.svg" }) {
      publicURL
    }
    contentfulGlobalGenerics(node_locale: { eq: $locale }) {
      node_locale
      viewConference
      homeIndexPageIntroduction {
        homeIndexPageIntroduction
      }
      imageCredit
      oops
      couldntFindPage
      findMore @include(if: $showAllContent)
      enjoyLatest @include(if: $showAllContent)
    }
  }
`;
